<script>
  import { onMount, afterUpdate, createEventDispatcher } from "svelte";
  import { Box, Stack, Cluster } from "creditdesign-svelte-components";
  import { SimpleButton } from "nature-immersive-svelte-components";

  export let timelineData = [];
  export let headline;
  export let standFirst;

  let mounted = false;
  let timelineElement;
  let timelineIndex = 0;
  let timelineWidth = 751;

  /* '-50%' intercept when the item is half way up the screen */
  let options = {
    root: null,
    rootMargin: "0px 0px 0px 0px",
  };

  let dispatch = createEventDispatcher();

  let handleNextClick = () => {
    timelineElement.scrollLeft += timelineWidth;
  };

  let handlePrevClick = () => {
    timelineElement.scrollLeft -= timelineWidth;
  };

  onMount(() => {
    mounted = true;
    dispatch("update");

    let timelineEntries = Array.from(
      timelineElement.querySelectorAll(".js-timeline-entry")
    );

    let onEnterScreen = (entries) => {
      let entriesOnScreen = entries.filter((entry) => entry.isIntersecting);
      if (entriesOnScreen.length) {
        entriesOnScreen.forEach((entry) => {
          //   let { id } = entry.target.dataset;
          //   intersectingStep.set(id);
          timelineIndex = timelineEntries.indexOf(entry.target);
        });
      }
    };

    const observer = new IntersectionObserver(onEnterScreen, options);

    timelineEntries.forEach((entry) => observer.observe(entry));
  });

  afterUpdate(() => {
    dispatch("update");
  });
</script>

<style>
  :global(:root) {
    --color--nature-box-background: #f7f7f7;
    --color--text-highlight: #feeab2;
  }

  .nature-interactive-graphic {
    background-color: var(--color--nature-box-background);
    border-top: 1px solid var(--color--black-0);
  }

  blockquote {
    font-family: ui-monospace, Menlo, Monaco, "Courier New", monospace;
    margin-inline: 0;
    padding: var(--s0);
    background-color: var(--color--nature-box-background);
  }

  blockquote p {
    margin-block: 0;
    text-indent: 2em;
    font-style: italic;
  }

  :global(blockquote em) {
    background-color: var(--color--text-highlight);
  }

  [aria-label="carousel"] {
    max-width: 100%;
    overflow-x: scroll;

    scroll-behavior: smooth;
    scroll-snap-type: x mandatory;

    border-top: 1px solid var(--color--black-0);
    border-bottom: 1px solid var(--color--black-0);

    margin-inline-start: var(--s0);
    margin-inline-end: var(--s0);

    background-color: var(--color--nature-box-background);
  }

  [aria-label="carousel"] ul {
    list-style: none;
    display: flex;
    margin: 0;
    padding: 0;
    gap: var(--s1);
    max-width: none;
  }

  [aria-label="carousel"] li {
    flex: 0 0 100%;
    scroll-snap-align: start;
    max-width: none;
    background-color: var(--color--white-0);
    padding-block-start: var(--s1);
    padding-block-end: var(--s2);
    padding-inline-start: var(--s0);
    padding-inline-end: var(--s0);
  }

  @media (max-width: 450px) {
    [aria-label="carousel"] {
      background-color: var(--color--white-0);
    }

    [aria-label="carousel"] {
      margin-inline-start: 0;
      margin-inline-end: 0;
    }

    [aria-label="carousel"] li {
      padding-inline-start: 0;
      padding-inline-end: 0;
    }
  }
</style>

<main class="nature-interactive-graphic font-family:sans-serif">
  <Stack>
    <Box boxSpace="var(--s0)">
      <Stack>
        <h1 class="font-size:big-1">
          {@html headline}
        </h1>
        {#each standFirst as paragraph}
          <p>
            {@html paragraph}
          </p>
        {/each}
        <p id="instructions">
          {#if mounted}
            Scroll horizontally or use the buttons to move through the timeline
            of WHO statements about transmission of COVID-19:
          {:else}
            Scroll horizontally to move through the timeline of WHO statements
            about transmission of COVID-19:
          {/if}
        </p>
      </Stack>
    </Box>

    <div
      role="region"
      aria-label="carousel"
      aria-describedby="instructions"
      tabindex="0"
      bind:clientWidth={timelineWidth}
      bind:this={timelineElement}
    >
      <ul>
        {#each timelineData as { date, quotes, caption }}
          <li>
            <Stack className="js-timeline-entry">
              <h2 class="font-size:base">{@html date}</h2>

              <p class="caption">{@html caption}</p>

              <blockquote>
                {#each quotes as quote}
                  <p>
                    {@html quote}
                  </p>
                {/each}
              </blockquote>
            </Stack>
          </li>
        {/each}
      </ul>
    </div>

    {#if mounted}
      <div aria-label="carousel controls">
        <Box boxSpace="var(--s0)">
          <Cluster list="true">
            <li>
              <SimpleButton
                message="Previous"
                disabled={timelineIndex === 0 ? true : null}
                on:click={handlePrevClick}
              />
            </li>
            <li>
              <SimpleButton
                message="Next"
                disabled={timelineIndex === timelineData.length - 1
                  ? true
                  : null}
                on:click={handleNextClick}
              />
            </li>
          </Cluster>
        </Box>
      </div>
    {/if}
  </Stack>
</main>
